import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AnagraficaOcrReviewField } from './anagrafica-ocr';

/** 识别字段外框属性。 */
export interface ArchiveOcrFieldFrameProps {
    /** 当前字段的核对信息；没有识别结果时只渲染输入框。 */
    field?: AnagraficaOcrReviewField;
    /** 是否与原件当前高亮为同一字段。 */
    active?: boolean;
    /** 逐项确认。 */
    onConfirm?: (fieldId: string) => void;
    /** 冲突时采用识别值。 */
    onAdopt?: (fieldId: string) => void;
    /** 聚焦后让原件滚到对应区域。 */
    onFocus?: (fieldId: string) => void;
    /** 原有 CommonInput。 */
    children: React.ReactNode;
}

/**
 * 在输入框上叠加置信度、确认控件、冲突对比和核对状态。
 * @param props 字段核对信息与操作
 */
export function ArchiveOcrFieldFrame({
    field,
    active = false,
    onConfirm,
    onAdopt,
    onFocus,
    children,
}: ArchiveOcrFieldFrameProps): JSX.Element {
    const { t } = useTranslation();
    if (!field) {
        return <>{children}</>;
    }
    /** 置信度百分比，用于三档标签。 */
    const percent = Math.round(field.confidence * 100);
    /** 低置信且未核对时提示对照原件。 */
    const pendingText = field.level === 'low'
        ? t('archive_form_ocr_low_confidence')
        : t('archive_form_ocr_pending');
    /** 冲突且尚未改用识别值时展示两侧对比。 */
    const showConflict = field.status === 'conflict' && !field.confirmed;
    return (
        <Box
            className={[
                'archive-ocr-field',
                `archive-ocr-field--${field.level}`,
                field.confirmed ? 'archive-ocr-field--done' : '',
                active ? 'archive-ocr-field--active' : '',
            ].filter(Boolean).join(' ')}
            data-ocr-field={field.fieldId}
            // 聚焦与点击都通知原件对位（已聚焦再点同一字段时 focus 可能不触发）
            onFocusCapture={() => onFocus?.(field.fieldId)}
            onClickCapture={() => onFocus?.(field.fieldId)}
        >
            {children}
            <Box className="archive-ocr-field__marks">
                <Typography
                    component="span"
                    variant="caption"
                    className={`archive-ocr-field__confidence archive-ocr-field__confidence--${field.level}`}
                    data-testid={`archive-ocr-confidence-${field.level}`}
                >
                    {t(`archive_form_ocr_level_${field.level}`, { percent })}
                </Typography>
                <IconButton
                    size="small"
                    className={[
                        'archive-ocr-field__seal',
                        field.confirmed ? 'archive-ocr-field__seal--done' : '',
                    ].filter(Boolean).join(' ')}
                    aria-label={field.confirmed
                        ? t('archive_form_ocr_confirmed')
                        : t('archive_form_ocr_confirm_field')}
                    data-testid="archive-ocr-seal"
                    onClick={() => onConfirm?.(field.fieldId)}
                >
                    <Typography variant="caption" component="span">
                        {field.confirmed ? '✓' : '○'}
                    </Typography>
                </IconButton>
            </Box>
            {showConflict ? (
                <Box className="archive-ocr-field__conflict">
                    <Box className="archive-ocr-field__conflict-row">
                        <Typography variant="caption">{t('archive_form_ocr_current_value')}</Typography>
                        <Typography variant="subtitle2">{field.currentValue || t('archive_form_ocr_empty')}</Typography>
                    </Box>
                    <Box className="archive-ocr-field__conflict-row">
                        <Typography variant="caption">{t('archive_form_ocr_recognized_value')}</Typography>
                        <Typography variant="subtitle2">{field.valueOcr}</Typography>
                        <Button variant="text" onClick={() => onAdopt?.(field.fieldId)}>
                            {t('archive_form_ocr_adopt')}
                        </Button>
                    </Box>
                </Box>
            ) : null}
            <Typography
                variant="caption"
                className={field.confirmed ? 'archive-ocr-field__status archive-ocr-field__status--done' : 'archive-ocr-field__status'}
            >
                {field.confirmed ? t('archive_form_ocr_confirmed') : pendingText}
                {field.modified
                    ? ` ${t('archive_form_ocr_modified', { value: field.valueOcr })}`
                    : ''}
            </Typography>
        </Box>
    );
}
